// src/pages/Lenders.jsx
// SEO-optimised lenders page for Dynesourcer
// Target keywords: buy-to-let lender portal, list BTL mortgage products, BTL mortgage platform,
// buy-to-let mortgage distribution, specialist BTL lender, mortgage product upload, lender analytics

import React from "react";
import TopNav from "../../components/TopNav.jsx";
import SiteFooter from "../../components/SiteFooter.jsx";
import {
  Upload,
  BarChart3,
  Shield,
  Check,
  ArrowRight,
  FileSpreadsheet,
  PencilLine,
  Trash2,
  Eye,
  TrendingUp,
  Clock,
  Users,
  Layers,
  BadgeCheck,
} from "lucide-react";
import { Link } from "react-router-dom";

const LENDERS_HERO_SRC = "/Lenders.webp";

export default function Lenders() {
  React.useEffect(() => {
    document.title = "List Buy-to-Let Mortgage Products | BTL Lender Portal | Dynesourcer";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Publish your buy-to-let mortgage products on Dynesourcer's lender portal. Manual entry or CSV bulk upload, real-time analytics on impressions, shortlists and applications. Reach qualified landlord borrowers direct."
      );
    }
  }, []);

  React.useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = LENDERS_HERO_SRC;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  return (
    <main className="min-h-screen bg-white text-slate-800">
      <TopNav />

      {/* ── Hero ── */}
      <section
        className="relative h-[520px] w-full overflow-hidden"
        aria-label="Buy-to-let lender portal — list and manage BTL mortgage products"
      >
        <h1 className="sr-only">
          Buy-to-Let Lender Portal — List, Manage &amp; Analyse Your BTL Mortgage Products | Dynesourcer
        </h1>
        <img
          src={LENDERS_HERO_SRC}
          alt="Buy-to-let lender portal — publish mortgage products and track performance on Dynesourcer"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6">
          <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold uppercase tracking-widest mb-4">
            For BTL mortgage lenders
          </div>
          <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-tight max-w-4xl">
            Publish your BTL products.<br className="hidden md:block" /> Reach qualified landlords.
          </h2>
          <p className="mt-5 max-w-2xl text-white/90 text-lg leading-relaxed">
            List your buy-to-let mortgage products on Dynesourcer's sourcing platform — upload
            manually or via CSV, manage your live product range, and track real-time performance
            analytics from your dedicated lender portal.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 items-center justify-center">
            <Link
              to="/signin?force=true&next=/lender/overview"
              className="inline-flex items-center gap-2 rounded-lg bg-sky-500 px-7 py-3.5 text-base font-bold text-white hover:bg-sky-600 transition-colors shadow-lg"
            >
              Access lender portal <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href="mailto:admin@dynesourcer.co.uk?subject=Lender%20Listing%20Enquiry&body=Hi%20Dynesourcer%20team%2C%0A%0AI%20would%20like%20to%20enquire%20about%20listing%20our%20buy-to-let%20mortgage%20products%20on%20your%20platform.%0A%0APlease%20get%20in%20touch.%0A%0AKind%20regards%2C"
              className="inline-flex items-center gap-2 rounded-lg bg-white/15 backdrop-blur-sm border border-white/30 px-7 py-3.5 text-base font-semibold text-white hover:bg-white/25 transition-colors"
            >
              Enquire about listing
            </a>
          </div>
        </div>
      </section>

      {/* ── Trust bar ── */}
      <div className="text-white py-3" style={{ backgroundColor: "rgb(2 132 199)" }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm font-semibold">
            {[
              "Manual & CSV bulk upload",
              "Real-time product management",
              "Live performance analytics",
              "Broker-triaged applications only",
              "Full audit trail & compliance logs",
            ].map((item) => (
              <span key={item} className="flex items-center gap-2">
                <Check className="h-4 w-4 flex-shrink-0" /> {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Why lenders list with Dynesourcer ── */}
      <section
        className="py-16 md:py-24 bg-white"
        aria-label="Why BTL mortgage lenders choose Dynesourcer"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              A direct channel to qualified BTL borrowers
            </h2>
            <p className="mt-3 text-lg text-slate-600">
              Dynesourcer connects your buy-to-let mortgage products to landlords, portfolio
              investors and property professionals who are actively sourcing — filtered by
              eligibility, not just rate.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {/* Upload */}
            <div className="rounded-xl bg-white p-7 shadow-sm border border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600 mb-4">
                <Upload className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Fast, flexible product uploads
              </h3>
              <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                Publish new buy-to-let mortgage products in minutes. Enter manually for
                individual products or bulk upload your full range via CSV — whichever fits
                your workflow.
              </p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> Manual entry with rate, fees &amp; criteria fields</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> CSV bulk upload for full product ranges</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> Draft mode &amp; scheduled effective dates</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> Product badges, tags &amp; version history</li>
              </ul>
            </div>

            {/* Analytics */}
            <div className="rounded-xl bg-white p-7 shadow-sm border border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600 mb-4">
                <BarChart3 className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Real-time product performance analytics
              </h3>
              <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                See exactly how your buy-to-let mortgage products are performing — from first
                impression through to application. Use the data to price competitively and
                prioritise your highest-converting products.
              </p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> Impressions, clicks &amp; shortlist rates</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> Application volume &amp; approval rates</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> Time-to-cash trends by product</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> Drop-off &amp; conversion funnel data</li>
              </ul>
            </div>

            {/* Compliance */}
            <div className="rounded-xl bg-white p-7 shadow-sm border border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600 mb-4">
                <Shield className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Compliance, control &amp; audit trail
              </h3>
              <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                Every product edit, application and decision is logged with a full audit trail.
                Granular role-based permissions mean your team only sees what they need to.
              </p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> Role-based access control</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> Full product edit &amp; version audit log</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> Exportable compliance reports</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> SLA tracking &amp; response visibility</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Portal features ── */}
      <section
        className="py-16 md:py-20 bg-slate-50"
        aria-label="Dynesourcer lender portal features for managing buy-to-let mortgage products"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              Everything you need to manage your BTL product range
            </h2>
            <p className="mt-3 text-lg text-slate-600">
              Your lender portal gives you full control over your buy-to-let mortgage products
              from listing to reporting — no technical resource needed.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: <PencilLine className="h-5 w-5" />,
                title: "Manual product entry",
                desc: "Create individual buy-to-let mortgage products with full criteria — rate, LTV, ICR, property type, borrower profile, arrangement fee and more.",
              },
              {
                icon: <FileSpreadsheet className="h-5 w-5" />,
                title: "CSV bulk upload",
                desc: "Upload your entire BTL mortgage product range in one go using our standardised CSV template. Ideal for rate changes and full range refreshes.",
              },
              {
                icon: <Eye className="h-5 w-5" />,
                title: "View live listings",
                desc: "See exactly how your products appear to borrowers on the sourcing platform — rates, criteria, fees and eligibility requirements all displayed in real time.",
              },
              {
                icon: <PencilLine className="h-5 w-5" />,
                title: "Update products instantly",
                desc: "Edit rates, criteria or fees on any live product. Changes take effect immediately or on a scheduled date — with a full version history maintained.",
              },
              {
                icon: <Trash2 className="h-5 w-5" />,
                title: "Remove or archive products",
                desc: "Delist products that are no longer available or archive them for reference. Archived products remain in your analytics history but are hidden from borrowers.",
              },
              {
                icon: <TrendingUp className="h-5 w-5" />,
                title: "Performance dashboard",
                desc: "Track impressions, shortlists, applications and approval rates for each product. Identify your best performers and see where drop-off occurs in the funnel.",
              },
              {
                icon: <Clock className="h-5 w-5" />,
                title: "Time-to-cash reporting",
                desc: "Monitor average time from application to offer and completion for each product — helping you benchmark your service levels against market expectations.",
              },
              {
                icon: <Users className="h-5 w-5" />,
                title: "Broker-triaged applications",
                desc: "Every application reaching you has been reviewed by a qualified mortgage broker — reducing incomplete submissions, mismatched criteria and wasted underwriter time.",
              },
              {
                icon: <Layers className="h-5 w-5" />,
                title: "Multi-user team access",
                desc: "Invite team members with role-based permissions. Separate access levels for product managers, underwriters and compliance reviewers.",
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className="rounded-xl bg-white border border-slate-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-600 mb-3">
                  {feature.icon}
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works for lenders ── */}
      <section
        className="py-16 md:py-20 bg-white"
        aria-label="How to list buy-to-let mortgage products on Dynesourcer"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              How to list your BTL mortgage products on Dynesourcer
            </h2>
            <p className="mt-3 text-lg text-slate-600">
              From sign-up to live products in four straightforward steps.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-4">
            {[
              {
                num: "1",
                title: "Request lender access",
                desc: "Contact our team to set up your lender account. We'll onboard you to the portal with your branding, product templates and user permissions configured.",
              },
              {
                num: "2",
                title: "Upload your BTL products",
                desc: "Use manual entry for individual products or bulk upload your full range via CSV. Set rates, LTV, ICR, property type criteria and arrangement fees.",
              },
              {
                num: "3",
                title: "Go live on the sourcing platform",
                desc: "Your products are instantly visible to landlords and property investors sourcing on Dynesourcer — matched by eligibility, not just headline rate.",
              },
              {
                num: "4",
                title: "Receive broker-triaged applications",
                desc: "Applications come to you pre-reviewed by a qualified broker — with borrower profile, property details and supporting information already checked.",
              },
            ].map((step, idx) => (
              <div
                key={idx}
                className="rounded-xl bg-slate-50 border border-slate-200 p-6 hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-sky-500 text-white font-black text-lg mb-4">
                  {step.num}
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Quality of applications callout ── */}
      <section className="py-14 bg-sky-50 border-y border-sky-100">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-bold mb-4">
                <BadgeCheck className="h-4 w-4" /> Broker-triaged applications
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-4">
                Only qualified applications reach your underwriters
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Every application submitted through Dynesourcer is reviewed by a qualified
                mortgage broker before it reaches you. The broker checks eligibility against
                your product criteria, confirms the borrower profile, and validates the
                supporting information — so your underwriters spend time on cases that
                should complete, not chasing missing documents.
              </p>
              <ul className="space-y-3">
                {[
                  "Borrower profile matched to your product criteria before submission",
                  "Broker confirms ICR, LTV, property type and landlord experience",
                  "Incomplete or mismatched applications filtered out at triage",
                  "Reduces underwriter time wasted on unqualified cases",
                ].map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" /> {point}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-bold text-slate-900">Application triage status</h4>
                  <span className="text-xs font-semibold px-2 py-1 bg-sky-100 text-sky-700 rounded-full">Live</span>
                </div>
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="h-2 w-2 rounded-full bg-emerald-500" />
                      <span className="text-xs font-bold text-emerald-700">Broker validated — ready for underwriting</span>
                    </div>
                    <p className="text-xs text-slate-600">BTL purchase — 70% LTV — HMO — £380k — Portfolio landlord</p>
                  </div>
                  <div className="p-4 rounded-xl bg-sky-50 border border-sky-100">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="h-2 w-2 rounded-full bg-sky-500 animate-pulse" />
                      <span className="text-xs font-bold text-sky-700">Under broker review</span>
                    </div>
                    <p className="text-xs text-slate-600">BTL remortgage — 65% LTV — Ltd company SPV — £220k</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 opacity-60">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="h-2 w-2 rounded-full bg-slate-400" />
                      <span className="text-xs font-bold text-slate-500">Pending borrower documents</span>
                    </div>
                    <p className="text-xs text-slate-500">BTL purchase — 75% LTV — Individual landlord — £195k</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA band ── */}
      <section
        className="bg-sky-400 py-16 text-center text-white"
        aria-label="Get started listing buy-to-let mortgage products on Dynesourcer"
      >
        <h2 className="text-3xl md:text-4xl font-black">
          Ready to list your buy-to-let products?
        </h2>
        <p className="mt-3 text-white/90 max-w-xl mx-auto">
          Reach landlords and property investors who are actively sourcing — with broker-triaged
          applications and real-time performance analytics.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/signin?force=true&next=/lender/overview"
            className="inline-flex items-center gap-2 bg-sky-500 text-white font-bold px-7 py-3.5 rounded-lg hover:bg-sky-600 transition-colors shadow-lg"
          >
            Access lender portal <ArrowRight className="h-5 w-5" />
          </Link>
          <a
            href="mailto:admin@dynesourcer.co.uk?subject=Lender%20Listing%20Enquiry&body=Hi%20Dynesourcer%20team%2C%0A%0AI%20would%20like%20to%20enquire%20about%20listing%20our%20buy-to-let%20mortgage%20products%20on%20your%20platform.%0A%0APlease%20get%20in%20touch.%0A%0AKind%20regards%2C"
            className="inline-flex items-center gap-2 bg-white/15 border border-white/30 text-white font-semibold px-7 py-3.5 rounded-lg hover:bg-white/25 transition-colors"
          >
            Enquire about listing
          </a>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}